import { watch } from "vue";
import { useRoute } from "vue-router";
import { useTheme } from "@/composables/useTheme";

const { setTheme, resolvedTheme } = useTheme();

const allowedOrigin = import.meta.env.VITE_APP_URL || "";

export function useIntegratorThemeTool() {
  const route = useRoute();

  function openThemeEditorPopup() {
    setTheme(resolvedTheme.value);
    window.open(
      `/theme-editor-popup.html`,
      "ThemeEditorPopup",
      "width=900,height=700,menubar=no,toolbar=no,location=no,status=no",
    );
  }

  function processThemeEvent(event: MessageEvent) {
    if (
      event.origin !== allowedOrigin &&
      event.origin !== "https://account.screenfeed.com"
    )
      return;

    if (!event.data) return;

    if (event.data.type === "toggletheme") {
      setTheme(event.data.mode);
      // If switching to light mode, remove all CSS variables from body
      if (
        event.data.mode === "light" &&
        document.body.classList.contains("dark")
      ) {
        const style = document.body.style;
        // Remove all custom properties (CSS variables) from body
        for (let i = style.length - 1; i >= 0; i--) {
          const prop = style[i];
          if (typeof prop === "string" && prop.startsWith("--")) {
            style.removeProperty(prop);
          }
        }
      }
      return;
    }

    if (event.data.type === "setVariable" && event.data.variable) {
      // If dark mode is active, set on body (.dark class is on body), else on :root
      const isDark = document.body.classList.contains("dark");
      const target = isDark ? document.body : document.documentElement;
      target.style.setProperty(event.data.variable, event.data.value);
    }
  }

  function postThemeEventToParent(
    type: "toggletheme" | "setVariable",
    event: any,
  ) {
    if (!window.opener) {
      console.error("No parent window. Cannot reset theme mode.");
      return;
    }
    try {
      window.opener.postMessage({ type: type, ...event }, allowedOrigin);
    } catch (err) {
      console.error(`Error posting reset for theme mode: ${err}`);
    }
  }

  function postThemeModeToParent(mode: string) {
    postThemeEventToParent("toggletheme", { mode });
  }

  function postThemeVariableToParent(variable: string, value: string) {
    postThemeEventToParent("setVariable", { variable, value });
  }

  function registerThemeTool() {
    window.addEventListener("message", processThemeEvent);

    watch(
      () => route.query?.themeeditor,
      (val) => {
        if (val === "1") openThemeEditorPopup();
      },
      { immediate: true },
    );
  }

  function unregisterThemeTool() {
    window.removeEventListener("message", processThemeEvent);
  }

  return {
    registerThemeTool,
    unregisterThemeTool,
    postThemeVariableToParent,
    postThemeModeToParent,
  };
}
